/* MÂZIA wishlist (spec 01 §4.4). A list of product handles on this device only (localStorage
   'mz-wishlist', newest first; no account, no server). Loaded once per page as a module by layout/theme.liquid
   (hearts and links that arrive later, from the Section Rendering API, are synced by the observer below).

   Markup it drives:
   - [data-mz-wish="<handle>"]   heart buttons: aria-pressed + label (data-label-add / data-label-remove).
                                 A tap toggles the piece and never opens the product.
   - [data-mz-wish-count]        text = number of saved pieces (empty at 0); data-mz-wish-format="([count])".
   - [data-mz-wishlist-link]     the header heart and the menu row (links to /pages/wishlist, as designed):
                                 .is-saved (filled heart) while the list is not empty.
   - [data-mz-wishlist-open]     opens the drawer (none in the theme today; MZ.wishlist.open() does the same);
                                 also gets .is-saved.
   - #mz-wishlist                the drawer (snippets/mz-wishlist-drawer.liquid). When the page doesn't render
                                 it, it is loaded once from sections/mz-wishlist-drawer.liquid.
   Saved cards are rendered by Liquid: /products/<handle>?section_id=mz-product-card (the same card as the
   collection grid). Handles that 404 (deleted products) drop out; redirected handles are updated.

   API: window.MZ.wishlist = { list(), has(handle), count(), add(handle), remove(handle), toggle(handle),
   open(), close() }. Event: mz:wishlist { handles, count, handle, saved } after every change (also from
   other tabs). Opening sends mz:sheet-open { id: 'wishlist' }; the drawer closes on any other sheet-open. */

const KEY = 'mz-wishlist';
const MAX = 120;
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? [...new Set(list.filter((h) => typeof h === 'string' && h))] : [];
  } catch (error) {
    return [];
  }
}

function write(list) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch (error) {
    /* private mode or storage full: the list lives for this page only */
  }
}

let handles = read();

function emit(name, detail) {
  document.dispatchEvent(new CustomEvent(`mz:${name}`, { detail }));
}

function changed(handle, saved) {
  write(handles);
  sync();
  emit('wishlist', { handles: handles.slice(), count: handles.length, handle, saved });
}

const wishlist = {
  list: () => handles.slice(),
  has: (handle) => handles.includes(handle),
  count: () => handles.length,
  add(handle) {
    if (!handle) return;
    handles = [handle, ...handles.filter((h) => h !== handle)].slice(0, MAX);
    changed(handle, true);
  },
  remove(handle) {
    if (!handles.includes(handle)) return;
    handles = handles.filter((h) => h !== handle);
    changed(handle, false);
  },
  toggle(handle) {
    if (handles.includes(handle)) this.remove(handle);
    else this.add(handle);
    return handles.includes(handle);
  },
  open: (trigger) => openDrawer(trigger),
  close: () => closeDrawer(),
};

window.MZ = window.MZ || {};
window.MZ.wishlist = wishlist;

/* ---------- Keep every heart, count and link in sync ---------- */

function sync(scope = document) {
  const count = handles.length;
  for (const button of scope.querySelectorAll('[data-mz-wish]')) {
    const saved = handles.includes(button.dataset.mzWish);
    const pressed = String(saved);
    if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label && button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
  }
  if (scope !== document) return;
  for (const node of document.querySelectorAll('[data-mz-wish-count]')) {
    const format = node.dataset.mzWishFormat || '[count]';
    const text = count ? format.replace('[count]', String(count)) : '';
    if (node.textContent !== text) node.textContent = text;
  }
  for (const link of document.querySelectorAll('[data-mz-wishlist-open], [data-mz-wishlist-link]')) {
    link.classList.toggle('is-saved', count > 0);
  }
}

const WATCHED = '[data-mz-wish], [data-mz-wish-count], [data-mz-wishlist-open], [data-mz-wishlist-link]';
let syncQueued = false;
function queueSync() {
  if (syncQueued) return;
  syncQueued = true;
  requestAnimationFrame(() => {
    syncQueued = false;
    sync();
  });
}

// Cards arrive later (filters, "View more", recommendations, the drawer) or get morphed by Horizon.
new MutationObserver((records) => {
  for (const record of records) {
    if (record.type === 'attributes') {
      queueSync();
      return;
    }
    for (const node of record.addedNodes) {
      if (node.nodeType === 1 && (node.matches(WATCHED) || node.querySelector(WATCHED))) {
        queueSync();
        return;
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });

window.addEventListener('storage', (event) => {
  if (event.key !== KEY) return;
  handles = read();
  sync();
  emit('wishlist', { handles: handles.slice(), count: handles.length, handle: null, saved: null });
  if (drawer?.open) renderDrawer();
});

// bfcache: another page may have changed the list
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  handles = read();
  sync();
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const heart = target.closest('[data-mz-wish]');
  if (heart) {
    event.preventDefault();
    event.stopPropagation();
    wishlist.toggle(heart.dataset.mzWish);
    return;
  }

  const opener = target.closest('[data-mz-wishlist-open]');
  if (opener) {
    event.preventDefault();
    openDrawer(opener);
  }
});

/* ---------- Drawer ---------- */

let drawer = null;
let lastTrigger = null;
let loading = null;
const cards = new Map(); // handle -> card element (rendered by Liquid), or null when the product is gone

async function ensureDrawer() {
  if (drawer && drawer.isConnected) return drawer;
  drawer = document.getElementById('mz-wishlist');
  if (!drawer) {
    loading =
      loading ||
      fetch(`${root}?section_id=mz-wishlist-drawer`)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const node = doc.getElementById('mz-wishlist');
          if (node) document.body.append(node);
          return node;
        })
        .catch(() => null);
    drawer = await loading;
    loading = null;
  }
  if (drawer && !drawer.dataset.mzReady) wireDrawer(drawer);
  return drawer;
}

function wireDrawer(dialog) {
  dialog.dataset.mzReady = 'true';
  dialog.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-mz-wishlist-close]') || target === dialog) {
      event.preventDefault();
      closeDrawer();
    }
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDrawer();
  });
  dialog.addEventListener('close', () => {
    window.MZ?.unlockScroll?.('wishlist');
    document.documentElement.classList.remove('mz-wishlist-open');
  });
  // A link inside (a card, the empty-state button) navigates away: close first so bfcache restores it closed
  dialog.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link && !event.defaultPrevented && !link.target) closeDrawer({ restoreFocus: false });
  });
}

async function openDrawer(trigger) {
  lastTrigger = trigger || document.activeElement;
  const dialog = await ensureDrawer();
  if (!dialog) return;
  emit('sheet-open', { id: 'wishlist' });
  if (!dialog.open) {
    try {
      dialog.showModal();
    } catch (error) {
      dialog.setAttribute('open', '');
    }
    window.MZ?.lockScroll?.('wishlist');
    document.documentElement.classList.add('mz-wishlist-open');
    dialog.querySelector('[data-mz-wishlist-close]')?.focus({ preventScroll: true });
  }
  renderDrawer();
}

function closeDrawer({ restoreFocus = true } = {}) {
  if (!drawer || !drawer.open) return;
  drawer.close();
  emit('sheet-close', { id: 'wishlist' });
  if (restoreFocus && lastTrigger instanceof HTMLElement && lastTrigger.isConnected) {
    lastTrigger.focus({ preventScroll: true });
  }
}

document.addEventListener('mz:sheet-open', (event) => {
  if (event.detail?.id !== 'wishlist') closeDrawer({ restoreFocus: false });
});

async function fetchCard(handle) {
  if (cards.has(handle)) return cards.get(handle);
  try {
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=mz-product-card`);
    if (response.status === 404) {
      cards.set(handle, null);
      return null;
    }
    if (!response.ok) return undefined;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const card = doc.querySelector('.mz-card');
    if (!card) {
      // The section renders nothing for a product that no longer exists (or isn't published)
      cards.set(handle, null);
      return null;
    }
    // A renamed product redirects: keep the new handle
    const current = card.dataset.mzHandle;
    if (current && current !== handle) {
      handles = handles.map((h) => (h === handle ? current : h));
      handles = [...new Set(handles)];
      write(handles);
    }
    cards.set(current || handle, card);
    return card;
  } catch (error) {
    return undefined;
  }
}

function countText(dialog, count) {
  const template = count === 1 ? dialog.dataset.countOne : dialog.dataset.countOther;
  return (template || '[count]').replace('[count]', String(count));
}

let renderToken = 0;
async function renderDrawer() {
  const dialog = drawer;
  if (!dialog) return;
  const token = ++renderToken;
  const grid = dialog.querySelector('[data-mz-wl-grid]');
  const empty = dialog.querySelector('[data-mz-wl-empty]');
  const counter = dialog.querySelector('[data-mz-wl-count]');
  const list = handles.slice();

  if (counter) counter.textContent = countText(dialog, list.length);
  if (!list.length) {
    if (grid) grid.replaceChildren();
    if (empty) empty.hidden = false;
    dialog.removeAttribute('aria-busy');
    return;
  }
  if (empty) empty.hidden = true;
  dialog.setAttribute('aria-busy', 'true');

  const results = await Promise.all(list.map((handle) => fetchCard(handle)));
  if (token !== renderToken) return;

  // Drop deleted products from the list
  const gone = list.filter((handle, index) => results[index] === null);
  if (gone.length) {
    handles = handles.filter((h) => !gone.includes(h));
    changed(gone[0], false);
  }

  if (grid) {
    const items = [];
    results.forEach((card, index) => {
      if (!card) return;
      const item = document.createElement('li');
      item.className = 'mz-wl__item';
      item.dataset.handle = card.dataset.mzHandle || list[index];
      item.append(document.importNode(card, true));
      items.push(item);
    });
    grid.replaceChildren(...items);
    sync(grid);
  }
  if (counter) counter.textContent = countText(dialog, handles.length);
  if (empty) empty.hidden = handles.length > 0;
  dialog.removeAttribute('aria-busy');
}

// Un-hearting inside the open drawer removes the card at once; saving elsewhere refreshes it
document.addEventListener('mz:wishlist', (event) => {
  if (!drawer || !drawer.open) return;
  const { handle, saved } = event.detail || {};
  if (saved === false && handle) {
    const item = drawer.querySelector(`.mz-wl__item[data-handle="${CSS.escape(handle)}"]`);
    item?.remove();
    const counter = drawer.querySelector('[data-mz-wl-count]');
    if (counter) counter.textContent = countText(drawer, handles.length);
    const empty = drawer.querySelector('[data-mz-wl-empty]');
    if (empty) empty.hidden = handles.length > 0;
    return;
  }
  renderDrawer();
});

// Prices or stock change after a cart update: drop cached cards so the next open re-renders them
document.addEventListener('shopify:cart:lines-update', () => cards.clear());

// Phones: an add from a saved card (or from the options sheet opened on it) closes the wishlist sheet, so the
// bag's "Added to bag" toast and bag bar show (one sheet at a time). Desktop opens the bag drawer on top.
window.addEventListener(
  'shopify:cart:lines-update',
  (event) => {
    if (!drawer || !drawer.open || event.action !== 'add') return;
    if (window.matchMedia('(min-width: 750px)').matches) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !(drawer.contains(target) || target.closest('#quick-add-dialog'))) return;
    event.promise
      ?.then((result) => {
        if (!result?.detail?.didError) closeDrawer({ restoreFocus: false });
      })
      .catch(() => {});
  },
  true
);

sync();
